import type { LibraryMode } from "@/lib/tier";
import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  deleteLibraryItem,
  deriveLibraryTitle,
  findLikelyDuplicateLibraryItems,
  getLibrarySourceTypeLabel,
  moveLibraryItemToWorkspace,
  normalizeLibraryDraft,
  saveLibraryItem,
  setLibraryItemWorkspaces,
  type LibraryDuplicateWarning,
  type LibraryItemStatus,
} from "@/lib/library";
import {
  replaceLibraryItemCategories,
  type LibraryCategory,
} from "@/lib/libraryCategories";
import type { Workspace } from "@/hooks/useWorkspace";
import type { EditableDraft, LibraryRecord, PendingLibrarySave } from "./myLibraryHelpers";

type Args = {
  active: Workspace | null;
  libraryMode: LibraryMode;
  items: LibraryRecord[];
  setItems: Dispatch<SetStateAction<LibraryRecord[]>>;
  categories: LibraryCategory[];
  editingItem: LibraryRecord | null;
  setEditingItem: Dispatch<SetStateAction<LibraryRecord | null>>;
  setEditorOpen: Dispatch<SetStateAction<boolean>>;
  setImportOpen: Dispatch<SetStateAction<boolean>>;
  refreshLibrary: () => Promise<void>;
};

/** Owns the My Library mutation surface: save (with duplicate-detection
 *  prompt), bulk import, delete, move, and per-item status changes. */
export const useMyLibraryPersist = ({
  active,
  libraryMode,
  items,
  setItems,
  categories,
  editingItem,
  setEditingItem,
  setEditorOpen,
  setImportOpen,
  refreshLibrary,
}: Args) => {
  const [saving, setSaving] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState<LibraryDuplicateWarning[]>([]);
  const [pendingSave, setPendingSave] = useState<PendingLibrarySave | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingItem(null);
  }, [setEditingItem, setEditorOpen]);

  const persistLibraryItem = useCallback(async (config: PendingLibrarySave) => {
    if (!active) return;

    setSaving(true);
    try {
      const savedBase = await saveLibraryItem(active.id, config.draft, config.currentId);
      const previous = items.find((item) => item.id === savedBase.id) ?? editingItem;

      await replaceLibraryItemCategories(savedBase.id, config.categoryIds);
      const assignedCategories = categories
        .filter((category) => config.categoryIds.includes(category.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      let resolvedWorkspaceIds = previous?.workspaceIds ?? [savedBase.workspace_id];
      if (libraryMode === "shared" && config.workspaceIds) {
        await setLibraryItemWorkspaces(savedBase.id, savedBase.workspace_id, config.workspaceIds);
        resolvedWorkspaceIds = Array.from(new Set([savedBase.workspace_id, ...config.workspaceIds]));
      } else if (!config.currentId) {
        resolvedWorkspaceIds = [savedBase.workspace_id];
      }

      const saved: LibraryRecord = {
        ...savedBase,
        notes: config.draft.notes ?? null,
        // description column is encrypted-only; carry the draft value forward
        // so the detail card shows the synopsis without a refresh.
        description: config.draft.description ?? null,
        categories: assignedCategories,
        excerptCount: previous?.excerptCount ?? 0,
        workspaceIds: resolvedWorkspaceIds,
      };

      setItems((current) =>
        config.currentId
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [saved, ...current.filter((item) => item.id !== saved.id)],
      );

      if (config.closeEditor) closeEditor();
      setDuplicateWarnings([]);
      setPendingSave(null);
      setDuplicateDialogOpen(false);
      toast.success(config.successMessage);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [active, categories, closeEditor, editingItem, items, libraryMode, setItems]);

  const beginSave = useCallback((config: PendingLibrarySave) => {
    const ready = normalizeLibraryDraft(config.draft);
    if (!ready.title) {
      toast.error("Title is required");
      return;
    }
    const warnings = findLikelyDuplicateLibraryItems(ready, items, config.currentId);
    if (warnings.length) {
      setDuplicateWarnings(warnings);
      setPendingSave({ ...config, draft: ready });
      setDuplicateDialogOpen(true);
      return;
    }
    void persistLibraryItem({ ...config, draft: ready });
  }, [items, persistLibraryItem]);

  const handleImportSave = useCallback(async (drafts: EditableDraft[], categoryIds: string[]) => {
    if (!active || drafts.length === 0) return;
    setSaving(true);
    try {
      const normalizedDrafts = drafts.map((draft) => normalizeLibraryDraft(draft));
      const savedBases = await Promise.all(normalizedDrafts.map((draft) => saveLibraryItem(active.id, draft)));
      await Promise.all(savedBases.map((item) => replaceLibraryItemCategories(item.id, categoryIds)));

      const assignedCategories = categories
        .filter((category) => categoryIds.includes(category.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      setItems((current) => [
        ...savedBases.map((item, idx) => ({
          ...item,
          notes: normalizedDrafts[idx]?.notes ?? null,
          description: normalizedDrafts[idx]?.description ?? null,
          categories: assignedCategories,
          excerptCount: 0,
          workspaceIds: [item.workspace_id],
        })),
        ...current.filter((item) => !savedBases.some((saved) => saved.id === item.id)),
      ]);
      setImportOpen(false);
      toast.success(`Imported ${savedBases.length} source${savedBases.length === 1 ? "" : "s"}`);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [active, categories, setImportOpen, setItems]);

  const deleteItem = useCallback(async (item: LibraryRecord, onAfter?: () => void) => {
    if (!confirm(`Remove “${deriveLibraryTitle(item)}” from My Library?`)) return;
    setSaving(true);
    try {
      await deleteLibraryItem(item.id);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      onAfter?.();
      toast.success("Library item removed");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [setItems]);

  const deleteCurrentItem = useCallback(async () => {
    if (!editingItem) return;
    await deleteItem(editingItem, closeEditor);
  }, [closeEditor, deleteItem, editingItem]);

  const moveCurrentItem = useCallback(async (targetWorkspaceId: string) => {
    if (!editingItem || !targetWorkspaceId || targetWorkspaceId === editingItem.workspace_id) return;
    setSaving(true);
    try {
      await moveLibraryItemToWorkspace(editingItem.id, editingItem.workspace_id, targetWorkspaceId);
      toast.success("Source moved to new workspace");
      closeEditor();
      await refreshLibrary();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  }, [closeEditor, editingItem, refreshLibrary]);

  const handleChangeStatus = useCallback(async (itemId: string, status: LibraryItemStatus) => {
    const previous = items.find((item) => item.id === itemId)?.status;
    if (previous === status) return;
    setItems((current) => current.map((item) => (item.id === itemId ? { ...item, status } : item)));
    try {
      const { error } = await supabase.from("library_items").update({ status }).eq("id", itemId);
      if (error) throw error;
      const label = status === "to_read" ? "To read" : status === "reading" ? "Reading" : status === "read" ? "Read" : "Revisit";
      toast.success(`Status set to ${label}`);
    } catch (error) {
      if (previous) {
        setItems((current) => current.map((item) => (item.id === itemId ? { ...item, status: previous } : item)));
      }
      toast.error((error as Error).message);
    }
  }, [items, setItems]);

  const saveSearchResultDraft = useCallback((result: Parameters<typeof beginSave>[0]["draft"], initialCategoryIds: string[]) => {
    beginSave({
      draft: { ...result, notes: null },
      categoryIds: initialCategoryIds,
      closeEditor: false,
      successMessage: `${getLibrarySourceTypeLabel(result.sourceType)} saved to My Library`,
    });
  }, [beginSave]);

  return {
    saving,
    duplicateWarnings,
    pendingSave,
    duplicateDialogOpen,
    setDuplicateDialogOpen,
    persistLibraryItem,
    beginSave,
    handleImportSave,
    deleteItem,
    deleteCurrentItem,
    moveCurrentItem,
    handleChangeStatus,
    saveSearchResultDraft,
  };
};
